import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Printer, ArrowLeft, Loader2, FileText } from 'lucide-react';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';

const KaryarambhAdesh = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { workData } = location.state || {};
    const [currentUser, setCurrentUser] = useState(null);
    const [fetchedWorkData, setFetchedWorkData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('currentUser'));
        if (user) {
            setCurrentUser(user);
        }

        if (workData && workData.id) {
            const workNamesRef = ref(db, `ekamwatapzp/WorkNames`);
            onValue(workNamesRef, (snapshot) => {
                if (snapshot.exists()) {
                    const allWorkNames = snapshot.val();
                    const meetingNum = workData.meetingNumber;
                    const workId = workData.id;

                    // Priority 1: Direct lookup
                    let fetchedWork = allWorkNames[meetingNum]?.[workId];

                    // Priority 2: Robust Search
                    if (!fetchedWork) {
                        for (const mNum in allWorkNames) {
                            if (allWorkNames[mNum][workId]) {
                                fetchedWork = allWorkNames[mNum][workId];
                                break;
                            }
                        }
                    }

                    if (fetchedWork) {
                        setFetchedWorkData({
                            ...fetchedWork,
                            id: workId,
                            meetingNumber: meetingNum,
                            schemeName: fetchedWork.schemeName || workData?.schemeName || "योजना उपलब्ध नाही"
                        });
                    } else {
                        setFetchedWorkData(workData);
                    }
                } else {
                    setFetchedWorkData(workData);
                }
                setLoading(false);
            }, (error) => {
                console.error("Error fetching work details:", error);
                setFetchedWorkData(workData);
                setLoading(false);
            }, { onlyOnce: true });
        } else {
            setFetchedWorkData(workData);
            setLoading(false);
        }
    }, [workData]);

    const handlePrint = () => {
        window.print();
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
                <Loader2 className="w-12 h-12 text-[#22165f] animate-spin mb-4" />
                <p className="text-lg font-bold text-gray-700">माहिती लोड होत आहे...</p>
            </div>
        );
    }

    if (!fetchedWorkData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
                <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <h2 className="text-xl font-bold text-red-600 mb-4">माहिती उपलब्ध नाही (Data not available)</h2>
                    <p className="mb-6 text-gray-600">कृपया डॅशबोर्डवरून पुन्हा प्रयत्न करा.</p>
                    <button
                        onClick={() => navigate('/subhe-dashboard')}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        डॅशबोर्डवर परत जा
                    </button>
                </div>
            </div>
        );
    }

    const userName = currentUser
        ? `${currentUser.surname || ''} ${currentUser.firstName || ''} ${currentUser.middleName || ''}`.trim()
        : "";
    const userAddress = currentUser
        ? `मु.पो. ${currentUser.atPo || ''}, ता. ${currentUser.taluka || ''}, जि. ${currentUser.district || ''}`
        : "";
    const panNo = currentUser?.panNo || "";
    const gstNo = currentUser?.gstNo || "";
    const mobileNo = currentUser?.whatsappNo || "";

    const date = new Date().toLocaleDateString('mr-IN');
    const inwardNo = `जिपसा/बांध/निविदा/का.आ./${fetchedWorkData.meetingNumber}/${new Date().getFullYear()}`;
    const tenderAgreementNo = `B-1/ ${Math.floor(Math.random() * 1000)} / ${new Date().getFullYear()}-${(new Date().getFullYear() + 1).toString().slice(-2)}`;

    const formatAmount = (value, multiplier = 100000) => {
        const cleanVal = (value || "0").toString().replace(/,/g, '').trim();
        const amount = parseFloat(cleanVal);
        if (isNaN(amount)) return "0";
        // If amount is small (< 1000), it's likely Lakhs, so multiply
        // If amount is already large, it's likely Rupees, so just return formatted
        if (amount < 1000) return (amount * multiplier).toLocaleString();
        return (amount * (multiplier / 100000)).toLocaleString(); // Adjusted multiplier if already in rupees
    };

    return (
        <div className="min-h-screen bg-gray-50 py-10 print:bg-white print:py-0">
            {/* Professional Header - Hidden on Print */}
            <div className="bg-white border-b border-gray-100 shadow-sm px-6 py-4 flex justify-between items-center print:hidden mb-6 sticky top-0 z-50">
                <div className="flex items-center gap-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="group flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-xl hover:bg-gray-100 transition-all font-black text-xs border border-gray-200 active:scale-95"
                    >
                        <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center border border-gray-200 group-hover:border-blue-200 group-hover:text-blue-600 transition-all">
                            ←
                        </div>
                        MAINTAIN (मागे जा)
                    </button>
                    <div className="h-8 w-[1px] bg-gray-100"></div>
                    <div className="flex items-center gap-3">
                        <div className="bg-[#22165f] p-2 rounded-lg text-white shadow-lg">
                            <FileText size={20} />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900 leading-none">कार्यारंभ आदेश (Work Order)</h1>
                            <p className="text-xs text-gray-500 mt-1">सांगली जिल्हा परिषद / बांधका विभाग</p>
                        </div>
                    </div>
                </div>
                <button
                    onClick={handlePrint}
                    className="flex items-center gap-2 px-6 py-2.5 bg-[#22165f] text-white rounded-xl hover:bg-[#2d1f7a] transition-all font-black text-xs shadow-xl shadow-indigo-100 active:scale-95 border-b-4 border-indigo-900"
                >
                    <Printer size={18} />
                    प्रिंट करा (Print)
                </button>
            </div>

            {/* Document Content */}
            <div className="max-w-[1000px] mx-auto bg-white p-12 shadow-2xl print:shadow-none print:p-4 font-serif text-[13px] leading-tight text-black border-gray-100 border">

                {/* Header Logos & Title */}
                <div className="flex justify-between items-center mb-1">
                    <img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS8X3zR9E_wX-9Lh-0A-0F_U5z0L-7-1-9-A&s" alt="ZP Logo" className="w-16 h-16 object-contain" />
                    <div className="text-center flex-1">
                        <h1 className="text-lg font-bold">जिल्हा परिषद, सांगली</h1>
                        <h2 className="text-[15px] font-bold">बांधकाम विभाग</h2>
                        <p className="text-[12px]">मध्यवर्ती प्रशासकीय इमारत सांगली - मिरज रोड, पुष्पराज चौक, सांगली ता - मिरज जि - सांगली</p>
                    </div>
                    <img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ7y2C_lY6f4S_Q0G_n_i9y_Y_4_O_2-9A&s" alt="Swachh Bharat Logo" className="w-20 h-10 object-contain" />
                </div>

                <div className="flex justify-between text-[11px] mb-1">
                    <p>फोन नं - ०२३३-२३७२७१९</p>
                    <p>e-mail ID :- eeworkszpsangli@gmail.com</p>
                </div>

                <hr className="border-black border mb-1" />
                <h2 className="text-center text-[16px] font-bold mb-1 underline">कार्यारंभ आदेश</h2>
                <hr className="border-black border mb-1" />

                {/* Metadata Table */}
                <table className="w-full mb-1 border-collapse border border-black">
                    <tbody>
                        <tr>
                            <td className="border border-black p-1 text-[12px]">जा.क्रं/जिपसा/बांध/निविदा/का.आ./ <span className="font-bold">{fetchedWorkData.id || '22'}</span> दिनांक :- <span className="font-bold">{date}</span></td>
                            <td className="border border-black p-1 text-[12px]">निविदा करारपत्र क्रं. B-1/ <span className="font-bold">{Math.floor(Math.random() * 100)}</span> / 2024-25</td>
                        </tr>
                    </tbody>
                </table>

                {/* Account Head Section */}
                <table className="w-full mb-1 border-collapse border border-black">
                    <tbody>
                        <tr>
                            <td className="border border-black p-1 w-1/2">लेखाशिर्ष :- <span className="font-bold underline">{fetchedWorkData.schemeName}</span></td>
                            <td className="border border-black p-0">
                                <table className="w-full border-collapse">
                                    <tbody>
                                        <tr>
                                            <td className="border-r border-black p-1">काम वाटप बैठक क्रं.</td>
                                            <td className="border-r border-black p-1 text-center font-bold">{fetchedWorkData.meetingNumber || '4'}</td>
                                            <td className="border-r border-black p-1">काम क्रं.</td>
                                            <td className="p-1 text-center font-bold">{fetchedWorkData.workNumber || '5'}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* Contractor Section */}
                <div className="mb-2">
                    <p className="font-bold">प्रति,</p>
                    <p className="font-bold text-[14px]">मक्तेदाराचे नांव व पत्ता :- श्री. {userName}, {userAddress}</p>
                </div>

                {/* PAN/GST Section */}
                <table className="w-full mb-2 border-collapse border border-black text-center">
                    <tbody>
                        <tr>
                            <td className="border border-black p-1 w-1/5">पॅन कार्ड नं.</td>
                            <td className="border border-black p-1 w-1/4 font-bold">{panNo}</td>
                            <td className="border border-black p-1 w-1/6">GST No.</td>
                            <td className="border border-black p-1 font-bold">{gstNo}</td>
                            <td className="border border-black p-1 w-1/12">मो.नं.</td>
                            <td className="border border-black p-1 font-bold">{mobileNo}</td>
                        </tr>
                        <tr>
                            <td className="border border-black p-1 font-bold">कामाचे नांव</td>
                            <td colSpan="5" className="border border-black p-1 font-bold text-left">{fetchedWorkData.workName}</td>
                        </tr>
                    </tbody>
                </table>

                {/* Deposit Details Table */}
                <table className="w-full mb-2 border-collapse border border-black text-[12px]">
                    <tbody>
                        <tr>
                            <td className="border border-black p-1 w-[20%]">१.अनामत र.रु. (५%)</td>
                            <td className="border border-black p-1 w-[20%] font-bold text-right">{formatAmount(fetchedWorkData.tenderAmount, 5000)}</td>
                            <td className="border border-black p-1 w-[30%]">ऑन लाईन भरलेला PRN क्रं. व र.रु.</td>
                            <td colSpan="3" className="border border-black p-1 font-bold">PRN: {Math.floor(Math.random() * 1000000)}</td>
                        </tr>
                        <tr>
                            <td className="border border-black p-1">२.चलनाने भरणा</td>
                            <td className="border border-black p-1 font-bold text-right">५,०००</td>
                            <td className="border border-black p-1">DCC बँक खाते क्रं. - ७७३३</td>
                            <td className="border border-black p-1 w-1/6">दिनांक</td>
                            <td className="border border-black p-1 font-bold">{date}</td>
                        </tr>
                        <tr>
                            <td className="border border-black p-1">एकूण जमा र.रु.</td>
                            <td className="border border-black p-1 font-bold text-right">५,०००</td>
                            <td className="border border-black p-1">देयकातून वसूल करावयाची र.रु.</td>
                            <td colSpan="2" className="border border-black p-1 text-right font-bold">२०,०००</td>
                        </tr>
                        <tr>
                            <td className="border border-black p-1">३.परफॉर्मन्स सिक्युरिटी डिपॉझीट</td>
                            <td className="border border-black p-1 text-right font-bold">०</td>
                            <td className="border border-black p-1">परफॉर्मन्स सिक्युरिटी डिपॉझीटचलनाने भरणा र.रु.</td>
                            <td className="border border-black p-1 text-center font-bold">निरंक</td>
                            <td className="p-1">
                                {/* <table className="w-full">
                                    <tbody>
                                        <tr>
                                            <td className="p-0 border-r border-black pr-2">नि.र.</td>
                                            <td className="p-0 pl-2">{formatAmount(fetchedWorkData.tenderAmount)}</td>
                                        </tr>
                                    </tbody>
                                </table> */}
                            </td>
                        </tr>
                        <tr>
                            <td colSpan="3" className="border border-black p-1 font-bold">१.या कार्यालयाकडील मक्ते मंजूरी टिपणी दिनांक</td>
                            <td colSpan="2" className="border border-black p-1 font-bold">{new Date().getMonth() + 1}/{new Date().getFullYear()}</td>
                        </tr>
                    </tbody>
                </table>

                <p className="mb-2 text-[12px]">अनामत रक्कम व निविदा विषयक सर्व कागदपत्रे आपण पूर्ण केलेली असल्याने आपली वरील कामाची निविदा खालीलप्रमाणे मान्य करण्यात येत आहे</p>

                {/* Tender Rates Table */}
                <table className="w-full mb-2 border-collapse border border-black text-center text-[12px]">
                    <thead>
                        <tr>
                            <th className="border border-black p-1 w-1/4" rowSpan="2">१. निविदा प्रसिद्धी र.रु.</th>
                            <th className="border border-black p-1 w-1/4">GST १८ %</th>
                            <th className="border border-black p-1 w-1/4">विमा (०.५%)</th>
                            <th className="border border-black p-1 w-1/4">एकूण र.रु.</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="border border-black p-1 font-bold">{formatAmount(fetchedWorkData.tenderAmount)}</td>
                            <td className="border border-black p-1 font-bold">{formatAmount(fetchedWorkData.tenderAmount, 18000)}</td>
                            <td className="border border-black p-1 font-bold">{formatAmount(fetchedWorkData.tenderAmount, 500)}</td>
                            <td className="border border-black p-1 font-bold">{formatAmount(fetchedWorkData.tenderAmount, 118500)}</td>
                        </tr>
                        <tr>
                            <td className="border border-black p-1 font-bold text-center" colSpan="4">प्रशासकीय मान्यतेच्या अधीन राहून रक्कम रुपये</td>
                        </tr>
                    </tbody>
                </table>

                {/* Period Section */}
                <table className="w-full mb-3 border-collapse border border-black text-[12px]">
                    <tbody>
                        <tr>
                            <td className="border border-black p-1 w-1/2 font-bold">४. कामाची मुदत : ६ महिने व अंतिम दि. {new Date(new Date().setMonth(new Date().getMonth() + 6)).toLocaleDateString('mr-IN')}</td>
                            <td className="border border-black p-1 font-bold">५.दोष दायित्व कालावधी (DLP) : २४ महिने</td>
                        </tr>
                    </tbody>
                </table>

                {/* Terms and Conditions */}
                <div className="space-y-1 text-[11px] leading-snug">
                    <p>१. मा आयुक्त पुणे यांचेकडील दिनांक ०५/०९/२०१८ रोजीचे परिपत्रकानुसार कामाची मुदत संपण्यापूर्वी १५ दिवस आधी मुदतवाढीसाठी रितसर अर्ज सादर करणे आवश्यक आहे. विहित कालावधीत मुदतवाढ अर्ज सादर न केल्यास ज्या दिवशी मुदतवाढ अर्ज देणे अपेक्षित होते त्या तारखेपासून ते प्रत्यक्ष अर्ज प्राप्त झाल्याच्या दिनांकापर्यंत प्रतिदिन दंड र.रु. ५०/- आकारण्यात येईल. मुदतवाढी संदर्भात अंतिम निर्णय मा.अति.मुकाअ यांचा राहिल.</p>
                    <p>२. सदरचे काम पोट मक्तेदार यांचेमार्फत करता येणार नाही. सदरचे काम आपण स्वतः केले असलेबाबतचे आवश्यक कागदपत्रे मागणी केल्यास सादर करणे आपणवर बंधनकारक राहील.</p>
                    <p>३.करारनाम्यात नमूद केल्याप्रमाणे कंत्राटदाराने शासकीय विमा निधी महाराष्ट्र सांगली यांचेकडे कंत्राटी कामाचा व त्यांच्याकडे काम करण्यास नियुक्त केलेल्या कामगारांचा विमा उतरविणे अनिवार्य राहील. त्याबाबतचे प्रमाणपत्र कामाच्या पहिल्या देयकासोबत देणे आवश्यक आहे अन्यथा शासनाच्या प्रचलित नियमानुसार पहिल्या देयकातून विम्याच्या रक्कमेची वसूली केली जाईल.</p>
                    <p>४.उपरोक्त काम आपण उप अभियंता (इवद) पं.स. {fetchedWorkData.taluka || 'मिरज'} यांचे मार्गदर्शनाखाली त्वरीत सुरु करावे.</p>
                    <p>५.कामाच्या ठिकाणी कामाचा सविस्तर माहिती फलक लावले नंतरच प्रत्यक्ष लाईनआऊट देऊन काम चालू केले जाईल. याची नोंद घ्यावी.</p>
                    <p>६. ठेकेदाराने कामगारांचे / मजुरांचे देयक आधार क्रमांक जोडलेल्या बँक खात्यात जमा केल्याचे प्रमाणपत्र जमा करावयाचे आहे हे प्रमाणपत्र करार केल्यापासून ६० दिवसात जमा करावयाचे आहे. जर करारनाम्याची मुदत ६० दिवसापेक्षा कमी असल्यास १५ दिवसात जमा करावयाचे आहे.</p>
                    <p>७. सदर काम सुरु करण्यापूर्वी शासकीय विमा उतरविणे बंधनकारक आहे.</p>
                    <p>८. मक्तेदाराने कार्यारंभ आदेश झाल्यापासून १५ दिवसांमध्ये काम सुरु करणे बंधनकारक राहील. पुढील १५ दिवसांमध्ये कामाचा प्रगती अहवाल व छायाचित्र सादर करावे. उक्त कालावधीत जर काम सुरु झाले नाही तर आपण विलंबाबाबत योग्य खुलासा सादर करणे बंधनकारक राहील, खुलासा योग्य अथवा समाधानकारक नसल्यास आपला कार्यारंभ आदेश विनानोटीस रद्द केला जाईल याची नोंद घ्यावी.</p>
                </div>

                {/* Signature Section */}
                <div className="flex justify-end mt-8">
                    <div className="text-center font-bold">
                        <p>(भारती स. विराज)</p>
                        <p>कार्यकारी अभियंता (इ.व.द.)</p>
                        <p>जिल्हा परिषद, सांगली</p>
                    </div>
                </div>

                {/* Watermark Background (Optional) */}
                <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] pointer-events-none select-none z-0">
                    <img
                        src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS8X3zR9E_wX-9Lh-0A-0F_U5z0L-7-1-9-A&s"
                        alt="ZP Logo"
                        className="w-[400px]"
                    />
                </div>
            </div>
        </div>
    );
};

export default KaryarambhAdesh;
